import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import ReferenceModal from '@/components/Modal/Project/Reference';

import Project from '@/api/project';
import icAddNew from '@/assets/icons/add-new-user.png';

const { getProductReference } = Project();

export default function Reference() {
	const [search, setSearch] = useState('');
	const [newReference, setNewReference] = useState(false);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

	const oC = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { ordering: 'id' };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		return obj;
	}, [search]);

	const {
		data: d,
		isFetching,
		isFetched,
	} = useQuery({
		queryKey: ['refs', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getProductReference(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataCount =
		useMemo(() => {
			if (isFetched && d.data?.count) {
				oC.current = d.data.count;
				return d.data.count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results, [d]) || [];

	const columns = [
		{
			field: 'id',
			headerName: 'Nr',
			filterable: false,
			sortable: true,
			width: 64,
			renderCell: (index) => index.api.getRowIndexRelativeToVisibleRows(index.row.id + 1),
		},
		{
			field: 'reference',
			headerName: 'Referanse',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'status',
			headerName: 'Referanse anvendt',
			headerAlign: 'center',
			align: 'center',
			flex: 0.5,
		},
	];

	const onSaveReference = () => {};
	const onCreateCode = () => {};

	return (
		<>
			<BDHelmet title='Project Reference' description='BDS Samferdsel project reference' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk i dokument her ...'
							onChange={(e) => {
								setSearch(e.target.value);
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
						<button
							onClick={() => setNewReference(true)}
							title='Registrer ny bruker'
							className='relative flex items-center gap-2 min-w-[124px] h-10 btn'
						>
							<div className='flex items-center justify-center w-5 h-5 bg-white rounded-full'>
								<img src={icAddNew} />
							</div>
							Ny kode
						</button>
					</div>
					<div>
						<span className='font-bold'>Totalt </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>totalt</span>
					</div>
					<div className='mx-auto'>
						<DataGrid
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							// rowsPerPageOptions={[10, 20, 50]}
							className='h-full mx-auto mt-0 w-[640px]'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								cellClass += ' cell-fix';
								if (selectedRow?.id === params.row.id) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectedRow(params.row);
							}}
							initialState={{
								pagination: { paginationModel: { pageSize: 10 } },
							}}
							// rowCount={dataCount}
							paginationModel={paginationModel}
							// paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
							pageSizeOptions={[10, 20, 50, 99]}
						/>
					</div>
				</div>
				{!!selectedRow && (
					<ReferenceModal open={!!selectedRow} onSave={onSaveReference} data={selectedRow} onClose={() => setSelectedRow(false)} />
				)}
				{!!newReference && <ReferenceModal open={!!newReference} onSave={onCreateCode} onClose={() => setNewReference(false)} />}
			</div>
		</>
	);
}
